import React, { useEffect, useState } from "react";
import api, { BASE_URL } from "../../Utils/api";
import { Alert, Button, Form, InputGroup, Modal, Spinner } from "react-bootstrap";
import { ErroModal } from "../Generals";
import { FiCheckCircle, FiEye, FiEyeOff, FiLock, FiMail } from "react-icons/fi";
import "./RecuperacaoSenhaModal.css";


function RecuperacaoSenhaModal({ show, onHide, emailInicial = "", token = "" }) {
  const [email, setEmail] = useState(emailInicial);
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const redefinindo = Boolean(token);

  useEffect(() => {
    if (!show) return;
    setEmail(emailInicial || "");
    setSenha("");
    setConfirmacao("");
    setMostrarSenha(false);
    setErro("");
    setSucesso("");
  }, [show, emailInicial, token]);

  const solicitar = async (event) => {
    event.preventDefault();
    setErro("");
    setSucesso("");
    setCarregando(true);
    try {
      const { data } = await api.post(`${BASE_URL}/api/auth/recuperar-senha`, {
        email,
      });
      setSucesso(
        data?.message ||
          "Se o e-mail estiver cadastrado, você receberá as instruções em alguns minutos."
      );
    } catch (err) {
      setErro(
        err.response?.data?.message ||
          "Não foi possível solicitar a recuperação. Tente novamente."
      );
    } finally {
      setCarregando(false);
    }
  };

  const redefinir = async (event) => {
    event.preventDefault();
    setErro("");
    setSucesso("");

    if (senha.length < 6 || senha.length > 128) {
      setErro("A senha deve possuir entre 6 e 128 caracteres.");
      return;
    }
    if (senha !== confirmacao) {
      setErro("As senhas informadas não coincidem.");
      return;
    }

    setCarregando(true);
    try {
      const { data } = await api.post(`${BASE_URL}/api/auth/redefinir-senha`, {
        token,
        senha,
      });
      setSenha("");
      setConfirmacao("");
      setSucesso(data?.message || "Senha alterada com sucesso. Faça login.");
    } catch (err) {
      setErro(
        err.response?.data?.message ||
          "Não foi possível alterar a senha. Solicite um novo link."
      );
    } finally {
      setCarregando(false);
    }
  };

  const concluido = redefinindo && Boolean(sucesso);

  return (
    <Modal
      show={show}
      onHide={carregando ? undefined : onHide}
      centered
      backdrop={carregando ? "static" : true}
      dialogClassName="recuperacao-senha-dialog"
      contentClassName="recuperacao-senha-content"
    >
      <Modal.Header closeButton={!carregando} className="recuperacao-senha-header">
        <div>
          <span className="recuperacao-senha-eyebrow">Acesso ao Doc</span>
          <Modal.Title>
            {concluido
              ? "Senha alterada"
              : redefinindo
                ? "Criar nova senha"
                : "Recuperar senha"}
          </Modal.Title>
        </div>
      </Modal.Header>

      <Form onSubmit={redefinindo ? redefinir : solicitar}>
        <Modal.Body className="recuperacao-senha-body">
          <ErroModal erro={erro} />
          {sucesso && (
            <Alert variant="success" className="d-flex align-items-start gap-2">
              <FiCheckCircle className="flex-shrink-0 mt-1" />
              <span>{sucesso}</span>
            </Alert>
          )}

          {!concluido && !redefinindo && (
            <>
              <p className="recuperacao-senha-description">
                Informe seu e-mail. Se houver uma conta com acesso por senha,
                enviaremos um link de uso único, válido por 30 minutos.
              </p>
              <Form.Group controlId="recuperacao-email">
                <Form.Label>Email</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FiMail /></InputGroup.Text>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="Seu e-mail"
                    autoComplete="email"
                    maxLength={255}
                    required
                    autoFocus
                  />
                </InputGroup>
              </Form.Group>
            </>
          )}

          {!concluido && redefinindo && (
            <>
              <p className="recuperacao-senha-description">
                Defina uma nova senha com no mínimo 6 caracteres.
              </p>
              <Form.Group controlId="recuperacao-nova-senha" className="mb-3">
                <Form.Label>Nova senha</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FiLock /></InputGroup.Text>
                  <Form.Control
                    type={mostrarSenha ? "text" : "password"}
                    value={senha}
                    onChange={(event) => setSenha(event.target.value)}
                    minLength={6}
                    maxLength={128}
                    autoComplete="new-password"
                    required
                    autoFocus
                  />
                  <Button
                    type="button"
                    variant="outline-secondary"
                    onClick={() => setMostrarSenha((valor) => !valor)}
                    aria-label={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
                  >
                    {mostrarSenha ? <FiEyeOff /> : <FiEye />}
                  </Button>
                </InputGroup>
              </Form.Group>
              <Form.Group controlId="recuperacao-confirmar-senha">
                <Form.Label>Confirme a nova senha</Form.Label>
                <InputGroup>
                  <InputGroup.Text><FiLock /></InputGroup.Text>
                  <Form.Control
                    type={mostrarSenha ? "text" : "password"}
                    value={confirmacao}
                    onChange={(event) => setConfirmacao(event.target.value)}
                    minLength={6}
                    maxLength={128}
                    autoComplete="new-password"
                    required
                  />
                </InputGroup>
              </Form.Group>
            </>
          )}
        </Modal.Body>

        <Modal.Footer className="modal-footer-padrao recuperacao-senha-footer">
          <Button
            type="button"
            className="btn-app-cancelar"
            onClick={onHide}
            disabled={carregando}
          >
            {concluido ? "Ir para o login" : "Cancelar"}
          </Button>
          {!concluido && (
            <Button
              type="submit"
              className="btn-app-primary"
              disabled={carregando}
            >
              {carregando && <Spinner animation="border" size="sm" />}
              {carregando
                ? "Aguarde..."
                : redefinindo
                  ? "Alterar senha"
                  : "Enviar instruções"}
            </Button>
          )}
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

export default RecuperacaoSenhaModal;
